import { useCallback, useEffect, useState } from 'react';

import type { Shop, Subscription } from '@notify/shared';

import { api } from '../../services/api';
import type { AvailablePlan, OwnerProfile, QRCodeResult, UsageStats } from '../../services/shop.service';
import { shopService } from '../../services/shop.service';

// ─── Types ────────────────────────────────────────────────────────────────────

type TabId = 'profile' | 'shop' | 'subscription';

interface Toast {
  id: number;
  type: 'success' | 'error';
  message: string;
}

// ─── Toast ────────────────────────────────────────────────────────────────────

function useToast() {
  const [toasts, setToasts] = useState<Toast[]>([]);

  function showToast(type: Toast['type'], message: string) {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, type, message }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 3500);
  }

  return { toasts, showToast };
}

function ToastContainer({ toasts }: { toasts: Toast[] }) {
  if (toasts.length === 0) return null;
  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={`rounded-lg px-4 py-3 text-sm font-medium text-white shadow-lg ${
            toast.type === 'success' ? 'bg-green-500' : 'bg-red-500'
          }`}
        >
          {toast.message}
        </div>
      ))}
    </div>
  );
}

// ─── Shared UI components ─────────────────────────────────────────────────────

function Field({
  label,
  value,
  onChange,
  type = 'text',
  error,
  placeholder,
  suffix,
  disabled = false,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  error?: string;
  placeholder?: string;
  suffix?: React.ReactNode;
  disabled?: boolean;
}) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-slate-700">{label}</label>
      <div className="relative">
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          className={`w-full rounded-lg border bg-slate-50 py-2 pl-3 text-sm text-slate-900 outline-none transition-colors focus:ring-1 ${
            error
              ? 'border-red-300 focus:border-red-400 focus:ring-red-200'
              : 'border-slate-200 focus:border-indigo-500 focus:ring-indigo-200'
          } ${suffix ? 'pr-10' : 'pr-3'} ${
            disabled ? 'cursor-not-allowed bg-slate-100 text-slate-500' : ''
          }`}
        />
        {suffix ? (
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 text-xs text-slate-500">
            {suffix}
          </div>
        ) : null}
      </div>
      {error ? <p className="mt-1 text-[11px] text-red-500">{error}</p> : null}
    </div>
  );
}

function EyeIcon({ open }: { open: boolean }) {
  return open ? (
    <svg
      className="h-4 w-4"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ) : (
    <svg
      className="h-4 w-4"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

function usePasswordVisibility() {
  const [visible, setVisible] = useState(false);
  return {
    inputType: visible ? 'text' : 'password',
    icon: (
      <button
        type="button"
        tabIndex={-1}
        onClick={() => setVisible((v) => !v)}
        className="text-slate-400 transition-colors hover:text-slate-600"
        aria-label={visible ? 'Hide password' : 'Show password'}
      >
        <EyeIcon open={visible} />
      </button>
    ),
  };
}

function LineSkeleton({ width = 'w-3/4' }: { width?: string }) {
  return <div className={`h-3.5 animate-pulse rounded bg-slate-200 ${width}`} />;
}

function FieldSkeleton() {
  return (
    <div className="space-y-2">
      <div className="h-3 w-12 animate-pulse rounded bg-slate-200" />
      <div className="h-9 animate-pulse rounded-lg bg-slate-100" />
    </div>
  );
}

function CardHeader({ icon, title }: { icon: React.ReactNode; title: string }) {
  return (
    <div className="border-b border-slate-100 px-6 py-4">
      <div className="flex items-center gap-2">
        <span className="text-indigo-500">{icon}</span>
        <p className="text-[14px] font-semibold text-slate-950">{title}</p>
      </div>
    </div>
  );
}

function getErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

// ─── Icons ────────────────────────────────────────────────────────────────────

const PersonIcon = (
  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const LockIcon = (
  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0110 0v4" />
  </svg>
);

const StoreIcon = (
  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);

const CardIcon = (
  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <rect x="2" y="5" width="20" height="14" rx="2" />
    <line x1="2" y1="10" x2="22" y2="10" />
  </svg>
);

const PlansIcon = (
  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
  </svg>
);

const QRIcon = (
  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <rect x="3" y="3" width="7" height="7" />
    <rect x="14" y="3" width="7" height="7" />
    <rect x="3" y="14" width="7" height="7" />
    <rect x="5" y="5" width="3" height="3" fill="currentColor" stroke="none" />
    <rect x="16" y="5" width="3" height="3" fill="currentColor" stroke="none" />
    <rect x="5" y="16" width="3" height="3" fill="currentColor" stroke="none" />
    <path d="M14 14h3v3h-3zM17 17h3v3h-3zM14 20h3" />
  </svg>
);

// ─── Profile Tab ─────────────────────────────────────────────────────────────

function ProfileTab({
  showToast,
}: {
  showToast: (type: Toast['type'], message: string) => void;
}) {
  const [profileLoading, setProfileLoading] = useState(true);
  const [originalProfile, setOriginalProfile] = useState<OwnerProfile | null>(null);
  const [profileName, setProfileName] = useState('');
  const [profileEmail, setProfileEmail] = useState('');
  const [profileNameError, setProfileNameError] = useState('');
  const [profileEmailError, setProfileEmailError] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);

  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [currentPwError, setCurrentPwError] = useState('');
  const [newPwError, setNewPwError] = useState('');
  const [confirmPwError, setConfirmPwError] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const currentPwVis = usePasswordVisibility();
  const newPwVis = usePasswordVisibility();
  const confirmPwVis = usePasswordVisibility();

  const fetchProfile = useCallback(async () => {
    try {
      const data = await shopService.getOwnerProfile();
      setOriginalProfile(data);
      setProfileName(data.name);
      setProfileEmail(data.email);
    } catch {
      // profile stays empty on error
    } finally {
      setProfileLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchProfile();
  }, [fetchProfile]);

  async function handleSaveProfile(event: React.FormEvent) {
    event.preventDefault();
    let hasError = false;
    setProfileNameError('');
    setProfileEmailError('');

    if (!profileName.trim()) {
      setProfileNameError('Name is required');
      hasError = true;
    }
    if (!profileEmail.trim()) {
      setProfileEmailError('Email is required');
      hasError = true;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profileEmail)) {
      setProfileEmailError('Enter a valid email address');
      hasError = true;
    }
    if (hasError) return;

    const payload: { name?: string; email?: string } = {};
    if (profileName.trim() !== originalProfile?.name) payload.name = profileName.trim();
    if (profileEmail.trim() !== originalProfile?.email) payload.email = profileEmail.trim();

    if (Object.keys(payload).length === 0) {
      showToast('success', 'No changes to save');
      return;
    }

    setProfileSaving(true);
    try {
      await shopService.updateOwnerProfile(payload);
      showToast('success', 'Profile saved');
      await fetchProfile();
    } catch (error) {
      const message = getErrorMessage(error, 'Failed to save profile');
      if (message.toLowerCase().includes('email')) {
        setProfileEmailError(message);
      } else {
        showToast('error', message);
      }
    } finally {
      setProfileSaving(false);
    }
  }

  async function handleUpdatePassword(event: React.FormEvent) {
    event.preventDefault();
    let hasError = false;
    setCurrentPwError('');
    setNewPwError('');
    setConfirmPwError('');

    if (!currentPw) {
      setCurrentPwError('Current password is required');
      hasError = true;
    }
    if (newPw.length < 8) {
      setNewPwError('Must be at least 8 characters');
      hasError = true;
    } else if (!/\d/.test(newPw)) {
      setNewPwError('Must contain at least one number');
      hasError = true;
    }
    if (!confirmPw) {
      setConfirmPwError('Please confirm your new password');
      hasError = true;
    } else if (newPw !== confirmPw) {
      setConfirmPwError('Passwords do not match');
      hasError = true;
    }
    if (hasError) return;

    setPwSaving(true);
    try {
      await api.patch<{ message: string }>('/api/v1/auth/password', {
        currentPassword: currentPw,
        newPassword: newPw,
      });
      setCurrentPw('');
      setNewPw('');
      setConfirmPw('');
      showToast('success', 'Password updated');
    } catch (error) {
      const message = getErrorMessage(error, 'Failed to update password');
      if (message.toLowerCase().includes('current')) {
        setCurrentPwError(message);
      } else {
        showToast('error', message);
      }
    } finally {
      setPwSaving(false);
    }
  }

  return (
    <div className="space-y-5">
      {/* Owner Details */}
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <CardHeader icon={PersonIcon} title="Owner Details" />
        <div className="px-6 py-5">
          {profileLoading ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <FieldSkeleton />
              <FieldSkeleton />
            </div>
          ) : (
            <form onSubmit={(e) => void handleSaveProfile(e)} noValidate>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field
                  label="Name"
                  value={profileName}
                  onChange={setProfileName}
                  error={profileNameError}
                  placeholder="Your name"
                />
                <Field
                  label="Email"
                  value={profileEmail}
                  onChange={setProfileEmail}
                  type="email"
                  error={profileEmailError}
                  placeholder="owner@example.com"
                />
              </div>
              <div className="mt-4">
                <button
                  type="submit"
                  disabled={profileSaving}
                  className="rounded-lg bg-indigo-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-indigo-600 disabled:opacity-60"
                >
                  {profileSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Change Password */}
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <CardHeader icon={LockIcon} title="Change Password" />
        <div className="px-6 py-5">
          <form onSubmit={(e) => void handleUpdatePassword(e)} noValidate className="space-y-4">
            <Field
              label="Current password"
              value={currentPw}
              onChange={setCurrentPw}
              type={currentPwVis.inputType}
              error={currentPwError}
              suffix={currentPwVis.icon}
            />
            <Field
              label="New password"
              value={newPw}
              onChange={setNewPw}
              type={newPwVis.inputType}
              error={newPwError}
              placeholder="Min 8 chars, must include a number"
              suffix={newPwVis.icon}
            />
            <Field
              label="Confirm new password"
              value={confirmPw}
              onChange={setConfirmPw}
              type={confirmPwVis.inputType}
              error={confirmPwError}
              suffix={confirmPwVis.icon}
            />
            <button
              type="submit"
              disabled={pwSaving}
              className="rounded-lg bg-indigo-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-indigo-600 disabled:opacity-60"
            >
              {pwSaving ? 'Updating...' : 'Update Password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

// ─── Shop Tab ─────────────────────────────────────────────────────────────────

function ShopTab({
  showToast,
}: {
  showToast: (type: Toast['type'], message: string) => void;
}) {
  const [shopLoading, setShopLoading] = useState(true);
  const [originalShop, setOriginalShop] = useState<Shop | null>(null);
  const [shopName, setShopName] = useState('');
  const [shopLocation, setShopLocation] = useState('');
  const [shopNameError, setShopNameError] = useState('');
  const [shopSaving, setShopSaving] = useState(false);

  const [qrLoading, setQrLoading] = useState(true);
  const [qrData, setQrData] = useState<QRCodeResult | null>(null);

  const fetchShop = useCallback(async () => {
    try {
      const data = await shopService.getMyShop();
      setOriginalShop(data);
      setShopName(data.name);
      setShopLocation(data.location);
    } catch {
      // shop stays empty on error
    } finally {
      setShopLoading(false);
    }
  }, []);

  const fetchQR = useCallback(async () => {
    try {
      const data = await shopService.getMyQRCode();
      setQrData(data);
    } catch {
      // qr stays null on error
    } finally {
      setQrLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchShop();
    void fetchQR();
  }, [fetchShop, fetchQR]);

  async function handleSaveShop(event: React.FormEvent) {
    event.preventDefault();
    setShopNameError('');

    if (!shopName.trim()) {
      setShopNameError('Shop name is required');
      return;
    }

    const payload: { name?: string; location?: string } = {};
    if (shopName.trim() !== originalShop?.name) payload.name = shopName.trim();
    if (shopLocation.trim() !== originalShop?.location) payload.location = shopLocation.trim();

    if (Object.keys(payload).length === 0) {
      showToast('success', 'No changes to save');
      return;
    }

    setShopSaving(true);
    try {
      const updated = await shopService.updateShopDetails(payload);
      setOriginalShop(updated);
      showToast('success', 'Shop details saved');
    } catch (error) {
      showToast('error', getErrorMessage(error, 'Failed to save shop details'));
    } finally {
      setShopSaving(false);
    }
  }

  function downloadQR() {
    if (!qrData?.qr_image_url || !qrData?.token) return;
    const link = document.createElement('a');
    link.href = qrData.qr_image_url;
    link.download = `notify-qr-${qrData.token}.png`;
    link.click();
  }

  function printQR() {
    if (!qrData?.qr_image_url) return;
    const qrImageUrl = qrData.qr_image_url;
    const printWindow = window.open('', '_blank');
    printWindow?.document.write(`
      <html><body style="text-align:center;padding:40px">
        <h2 style="font-family:sans-serif">Scan to track your order</h2>
        <img src="${qrImageUrl}" width="300" height="300"/>
        <p style="font-family:sans-serif;color:#666">Powered by Notify — notifyme.buzz</p>
      </body></html>
    `);
    printWindow?.print();
  }

  function RushModeBadge({ mode }: { mode: string }) {
    if (mode === 'heavy_rush')
      return (
        <span className="rounded-full bg-red-50 px-2.5 py-1 text-[11px] font-semibold text-red-700">
          Heavy Rush
        </span>
      );
    if (mode === 'busy')
      return (
        <span className="rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-700">
          Busy
        </span>
      );
    return (
      <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-600">
        Normal
      </span>
    );
  }

  function formatLinkedAt(dateStr: string): string {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  }

  const placeholderQR = (
    <svg width="180" height="180" viewBox="0 0 180 180" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="180" height="180" fill="#F1F5F9"/>
      <rect x="20" y="20" width="60" height="60" rx="4" fill="#CBD5E1"/>
      <rect x="100" y="20" width="60" height="60" rx="4" fill="#CBD5E1"/>
      <rect x="20" y="100" width="60" height="60" rx="4" fill="#CBD5E1"/>
      <rect x="35" y="35" width="30" height="30" rx="2" fill="#94A3B8"/>
      <rect x="115" y="35" width="30" height="30" rx="2" fill="#94A3B8"/>
      <rect x="35" y="115" width="30" height="30" rx="2" fill="#94A3B8"/>
      <rect x="100" y="100" width="15" height="15" fill="#CBD5E1"/>
      <rect x="120" y="100" width="15" height="15" fill="#CBD5E1"/>
      <rect x="140" y="100" width="20" height="15" fill="#CBD5E1"/>
      <rect x="100" y="120" width="20" height="15" fill="#CBD5E1"/>
      <rect x="125" y="120" width="15" height="15" fill="#CBD5E1"/>
      <rect x="145" y="120" width="15" height="15" fill="#CBD5E1"/>
      <rect x="100" y="140" width="15" height="20" fill="#CBD5E1"/>
      <rect x="120" y="140" width="20" height="20" fill="#CBD5E1"/>
      <rect x="145" y="145" width="15" height="15" fill="#CBD5E1"/>
    </svg>
  );

  return (
    <div className="space-y-5">
      {/* Shop Details */}
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <CardHeader icon={StoreIcon} title="Shop Details" />
        <div className="px-6 py-5">
          {shopLoading ? (
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <FieldSkeleton />
                <FieldSkeleton />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="h-16 animate-pulse rounded-lg bg-slate-100" />
                <div className="h-16 animate-pulse rounded-lg bg-slate-100" />
              </div>
            </div>
          ) : (
            <>
              <form onSubmit={(e) => void handleSaveShop(e)} noValidate>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field
                    label="Shop Name"
                    value={shopName}
                    onChange={setShopName}
                    error={shopNameError}
                    placeholder="My Shop"
                  />
                  <Field
                    label="Location"
                    value={shopLocation}
                    onChange={setShopLocation}
                    placeholder="123 Main Street"
                  />
                </div>
                <div className="mt-4">
                  <button
                    type="submit"
                    disabled={shopSaving}
                    className="rounded-lg bg-indigo-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-indigo-600 disabled:opacity-60"
                  >
                    {shopSaving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>

              {originalShop ? (
                <div className="mt-6 border-t border-slate-100 pt-5">
                  <p className="mb-3 text-[11px] font-semibold uppercase tracking-widest text-slate-400">
                    Current Status
                  </p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="rounded-lg bg-slate-50 px-4 py-3">
                      <p className="text-[11px] text-slate-400">Rush Mode</p>
                      <div className="mt-1.5">
                        <RushModeBadge mode={originalShop.rushMode} />
                      </div>
                    </div>
                    <div className="rounded-lg bg-slate-50 px-4 py-3">
                      <p className="text-[11px] text-slate-400">Prep Time</p>
                      <p className="mt-0.5 text-[15px] font-semibold text-slate-950">
                        {originalShop.prepTimeMinutes} minutes
                      </p>
                    </div>
                  </div>
                  <p className="mt-3 text-[12px] text-slate-400">
                    Rush mode and prep time can be changed from the Dashboard.
                  </p>
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>

      {/* QR Code */}
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <CardHeader icon={QRIcon} title="QR Code" />
        <div className="px-6 py-5">
          {qrLoading ? (
            <div className="flex flex-col items-center gap-4">
              <div className="h-[180px] w-[180px] animate-pulse rounded-lg bg-slate-100" />
              <div className="w-full space-y-2 text-center">
                <div className="mx-auto h-3 w-1/3 animate-pulse rounded bg-slate-200" />
                <div className="mx-auto h-3 w-1/2 animate-pulse rounded bg-slate-200" />
              </div>
              <div className="flex gap-3">
                <div className="h-9 w-28 animate-pulse rounded-lg bg-slate-100" />
                <div className="h-9 w-28 animate-pulse rounded-lg bg-slate-100" />
              </div>
            </div>
          ) : qrData?.hasQR ? (
            <div className="flex flex-col items-center gap-4">
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                <img
                  src={qrData.qr_image_url ?? ''}
                  alt="Shop QR Code"
                  width={180}
                  height={180}
                  className="rounded"
                />
              </div>
              {qrData.token ? (
                <p className="font-mono text-[12px] text-slate-400">{qrData.token}</p>
              ) : null}
              {qrData.linked_at ? (
                <p className="text-[12px] text-slate-500">
                  Linked on {formatLinkedAt(qrData.linked_at)}
                </p>
              ) : null}
              <div className="flex flex-wrap justify-center gap-3">
                <button
                  type="button"
                  onClick={downloadQR}
                  className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:border-indigo-300 hover:text-indigo-600"
                >
                  Download QR
                </button>
                <button
                  type="button"
                  onClick={printQR}
                  className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:border-indigo-300 hover:text-indigo-600"
                >
                  Print QR
                </button>
              </div>
              <p className="text-center text-[12px] text-slate-400">
                Share this QR code at your counter. Customers scan it to track their orders.
              </p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-4">
              <div className="relative">
                <div style={{ filter: 'blur(6px)' }}>{placeholderQR}</div>
                <div className="absolute inset-0 flex flex-col items-center justify-center rounded-xl bg-white/70">
                  <svg
                    className="mb-2 h-6 w-6 text-slate-400"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    aria-hidden="true"
                  >
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0110 0v4" />
                  </svg>
                  <p className="text-[13px] font-semibold text-slate-700">QR Code Not Connected</p>
                </div>
              </div>
              <p className="max-w-xs text-center text-[12px] text-slate-500">
                Your shop needs a QR code to work with Notify. Please contact your Notify admin to
                complete setup.
              </p>
              <a
                href={`mailto:support@notifyme.buzz?subject=${encodeURIComponent(`QR Code Setup for ${originalShop?.name ?? 'My Shop'}`)}`}
                className="rounded-lg bg-indigo-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-indigo-600"
              >
                Contact Admin
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Subscription Tab ─────────────────────────────────────────────────────────

function SubscriptionTab({
  showToast,
}: {
  showToast: (type: Toast['type'], message: string) => void;
}) {
  const [subLoading, setSubLoading] = useState(true);
  const [usageLoading, setUsageLoading] = useState(true);
  const [plansLoading, setPlansLoading] = useState(true);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [usage, setUsage] = useState<UsageStats | null>(null);
  const [plans, setPlans] = useState<AvailablePlan[]>([]);

  useEffect(() => {
    void shopService
      .getMySubscription()
      .then((data) => {
        if ('id' in data) setSubscription(data as Subscription);
      })
      .finally(() => setSubLoading(false));

    void shopService.getUsage().then(setUsage).finally(() => setUsageLoading(false));

    void shopService
      .getAvailablePlans()
      .then(setPlans)
      .finally(() => setPlansLoading(false));
  }, []);

  function getDaysRemaining(expiresAt: Date | string): number {
    const diff = new Date(expiresAt).getTime() - Date.now();
    return Math.max(0, Math.ceil(diff / 86400000));
  }

  function formatDate(date: Date | string): string {
    return new Date(date).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  }

  const daysRemaining = subscription ? getDaysRemaining(subscription.expiresAt) : null;
  const daysColor =
    daysRemaining === null
      ? ''
      : daysRemaining < 3
        ? 'text-red-600'
        : daysRemaining < 7
          ? 'text-amber-600'
          : 'text-slate-950';

  function PlanBadge({ plan }: { plan: string }) {
    const styles: Record<string, string> = {
      trial: 'bg-blue-50 text-blue-700',
      basic: 'bg-slate-100 text-slate-700',
      pro: 'bg-indigo-50 text-indigo-700',
      enterprise: 'bg-purple-50 text-purple-700',
    };
    return (
      <span
        className={`rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize ${
          styles[plan] ?? 'bg-slate-100 text-slate-700'
        }`}
      >
        {plan}
      </span>
    );
  }

  function StatusBadge({ status }: { status: string }) {
    const styles: Record<string, string> = {
      active: 'bg-green-50 text-green-700',
      trial: 'bg-blue-50 text-blue-700',
      grace: 'bg-amber-50 text-amber-700',
      expired: 'bg-red-50 text-red-700',
      cancelled: 'bg-slate-100 text-slate-700',
    };
    return (
      <span
        className={`rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize ${
          styles[status] ?? 'bg-slate-100 text-slate-700'
        }`}
      >
        {status}
      </span>
    );
  }

  return (
    <div className="space-y-5">
      {/* Current Plan */}
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <CardHeader icon={CardIcon} title="Current Plan" />
        <div className="px-6 py-5">
          {subLoading || usageLoading ? (
            <div className="space-y-3">
              <LineSkeleton width="w-1/3" />
              <LineSkeleton width="w-1/2" />
              <LineSkeleton width="w-2/3" />
              <div className="h-2 animate-pulse rounded-full bg-slate-100" />
            </div>
          ) : subscription ? (
            <div className="space-y-4">
              {subscription.planName === 'trial' &&
              daysRemaining !== null &&
              daysRemaining < 7 ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] text-amber-800">
                  ⚠️ Your trial ends in {daysRemaining} day{daysRemaining !== 1 ? 's' : ''}.
                  Upgrade to keep using Notify.
                </div>
              ) : null}

              {subscription.status === 'expired' ? (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-800">
                  Your subscription has expired. Upgrade to continue sending notifications.
                </div>
              ) : null}

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-lg bg-slate-50 px-4 py-3">
                  <p className="text-[11px] text-slate-400">Plan</p>
                  <div className="mt-1.5">
                    <PlanBadge plan={subscription.planName} />
                  </div>
                </div>
                <div className="rounded-lg bg-slate-50 px-4 py-3">
                  <p className="text-[11px] text-slate-400">Status</p>
                  <div className="mt-1.5">
                    <StatusBadge status={subscription.status} />
                  </div>
                </div>
                <div className="rounded-lg bg-slate-50 px-4 py-3">
                  <p className="text-[11px] text-slate-400">Subscribed</p>
                  <p className="mt-0.5 text-[13px] font-medium text-slate-950">
                    {formatDate(subscription.subscribedAt)}
                  </p>
                </div>
                <div className="rounded-lg bg-slate-50 px-4 py-3">
                  <p className="text-[11px] text-slate-400">Expires</p>
                  <p className="mt-0.5 text-[13px] font-medium text-slate-950">
                    {formatDate(subscription.expiresAt)}
                  </p>
                </div>
              </div>

              {daysRemaining !== null ? (
                <div className="rounded-lg bg-slate-50 px-4 py-3">
                  <p className="text-[11px] text-slate-400">Days Remaining</p>
                  <p className={`mt-0.5 text-[22px] font-bold ${daysColor}`}>{daysRemaining}</p>
                </div>
              ) : null}

              {usage ? (
                <div>
                  <p className="mb-2 text-[11px] font-semibold uppercase tracking-widest text-slate-400">
                    Daily Usage
                  </p>
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between text-[13px]">
                      <span className="text-slate-700">Notifications today</span>
                      <span className="font-semibold text-slate-950">
                        {usage.used} / {usage.limit === null ? '∞' : usage.limit}
                      </span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                      <div
                        className="h-full rounded-full bg-indigo-500 transition-all"
                        style={{ width: `${Math.min(usage.percentUsed, 100)}%` }}
                      />
                    </div>
                    <p className="text-right text-[11px] text-slate-400">{usage.percentUsed}%</p>
                  </div>
                </div>
              ) : null}
            </div>
          ) : (
            <p className="text-[13px] text-slate-500">No active subscription found.</p>
          )}
        </div>
      </div>

      {/* Available Plans */}
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <CardHeader icon={PlansIcon} title="Available Plans" />
        <div className="px-6 py-5">
          {plansLoading ? (
            <div className="grid gap-4 md:grid-cols-2">
              {[0, 1, 2].map((i) => (
                <div key={i} className="space-y-3 rounded-xl border border-slate-200 p-5">
                  <LineSkeleton width="w-1/2" />
                  <LineSkeleton width="w-5/6" />
                  <LineSkeleton width="w-1/3" />
                  <LineSkeleton width="w-2/3" />
                </div>
              ))}
            </div>
          ) : plans.length === 0 ? (
            <p className="text-[13px] text-slate-500">No plans available at this time.</p>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {plans.map((plan) => {
                const monthCount = Math.max(1, Math.round(plan.duration_days / 30));
                const isCurrentPlan = subscription?.planName === plan.name;
                return (
                  <div
                    key={plan.id}
                    className={`rounded-xl border bg-white p-5 transition-all hover:border-indigo-200 hover:shadow-sm ${
                      plan.is_popular
                        ? 'border-l-4 border-slate-200 border-l-indigo-500'
                        : 'border-slate-200'
                    }`}
                  >
                    <div className="flex flex-wrap items-start gap-2">
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="text-[15px] font-semibold text-slate-950">{plan.title}</h3>
                          {plan.is_popular ? (
                            <span className="rounded-full bg-indigo-50 px-2.5 py-1 text-[11px] font-semibold text-indigo-600">
                              Most Popular
                            </span>
                          ) : null}
                        </div>
                        <p className="mt-1 text-[12px] text-slate-500">{plan.description}</p>
                      </div>
                    </div>

                    <div className="mt-3">
                      <p className="text-2xl font-bold text-slate-950">
                        ₹{plan.price_monthly}/month
                      </p>
                      <p className="mt-0.5 text-[12px] text-slate-400">
                        ₹{plan.price_monthly * monthCount} for {monthCount} month
                        {monthCount !== 1 ? 's' : ''}
                      </p>
                    </div>

                    <div className="mt-3 space-y-1.5">
                      {plan.features.slice(0, 4).map((feature) => (
                        <p key={feature} className="text-[12px] text-slate-700">
                          ✓ {feature}
                        </p>
                      ))}
                    </div>

                    <div className="mt-4">
                      {isCurrentPlan ? (
                        <button
                          type="button"
                          disabled
                          className="w-full cursor-not-allowed rounded-lg bg-slate-100 px-4 py-2 text-sm font-medium text-slate-500"
                        >
                          Current Plan
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() =>
                            showToast(
                              'success',
                              'Payment integration coming soon! Contact support@notifyme.buzz to upgrade.'
                            )
                          }
                          className="w-full rounded-lg bg-indigo-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-indigo-600"
                        >
                          Select Plan
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function ShopSettingsPage() {
  const { toasts, showToast } = useToast();
  const [activeTab, setActiveTab] = useState<TabId>('profile');

  const tabs: Array<{ id: TabId; label: string; icon: React.ReactNode }> = [
    {
      id: 'profile',
      label: 'Profile',
      icon: (
        <svg
          className="h-4 w-4"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
        >
          <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
      ),
    },
    {
      id: 'shop',
      label: 'Shop',
      icon: (
        <svg
          className="h-4 w-4"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
        >
          <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
          <polyline points="9 22 9 12 15 12 15 22" />
        </svg>
      ),
    },
    {
      id: 'subscription',
      label: 'Subscription',
      icon: (
        <svg
          className="h-4 w-4"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
        >
          <rect x="2" y="5" width="20" height="14" rx="2" />
          <line x1="2" y1="10" x2="22" y2="10" />
        </svg>
      ),
    },
  ];

  return (
    <div className="min-h-full bg-slate-50">
      <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8">
        <h1 className="mb-4 text-[20px] font-semibold text-slate-950">Settings</h1>

        {/* Tab bar */}
        <div style={{ borderBottom: '1px solid #E2E8F0', marginBottom: '24px', display: 'flex' }}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '12px 20px',
                fontSize: '14px',
                fontWeight: activeTab === tab.id ? 500 : 400,
                color: activeTab === tab.id ? '#6366F1' : '#64748B',
                background: 'none',
                border: 'none',
                borderBottom:
                  activeTab === tab.id ? '2px solid #6366F1' : '2px solid transparent',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'profile' && <ProfileTab showToast={showToast} />}
        {activeTab === 'shop' && <ShopTab showToast={showToast} />}
        {activeTab === 'subscription' && <SubscriptionTab showToast={showToast} />}
      </div>

      <ToastContainer toasts={toasts} />
    </div>
  );
}
